import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Tags, Pencil, Trash2, X, Check, Star } from "lucide-react";

const empty = { name: "", price: 0, currency: "AUD", interval: "one-time", description: "", features: [], highlighted: false, cta_label: "Get Started", cta_link: "", status: "active" };
const intervals = { "one-time": "one-time", session: "per session", month: "per month", year: "per year" };

export default function Plans() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [featureInput, setFeatureInput] = useState("");
  const [toDelete, setToDelete] = useState(null);

  const { data: plans = [], isLoading } = useQuery({ queryKey: ["plans"], queryFn: async () => (await api.get("/plans")).data });

  const save = useMutation({
    mutationFn: async (payload) => editing ? (await api.put(`/plans/${editing}`, payload)).data : (await api.post("/plans", payload)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["plans"] }); qc.invalidateQueries({ queryKey: ["dashboard-stats"] }); toast.success(editing ? "Plan updated" : "Plan created"); setOpen(false); },
    onError: () => toast.error("Could not save plan"),
  });
  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/plans/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["plans"] }); qc.invalidateQueries({ queryKey: ["dashboard-stats"] }); toast.success("Plan deleted"); setToDelete(null); },
  });

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (p) => { setEditing(p.id); setForm({ ...empty, ...p, features: p.features || [] }); setOpen(true); };
  const addFeature = () => { if (featureInput.trim()) { setForm((f) => ({ ...f, features: [...f.features, featureInput.trim()] })); setFeatureInput(""); } };
  const removeFeature = (i) => setForm((f) => ({ ...f, features: f.features.filter((_, x) => x !== i) }));

  return (
    <div data-testid="plans-page">
      <PageHeader
        title="Pricing & Plans"
        subtitle="Craft session packages and pricing tables your clients can explore in their portal."
        action={<Button data-testid="new-plan-button" onClick={openNew} className="rounded-full bg-gold-500 px-6 text-white hover:bg-gold-600 shadow-ambient"><Plus className="mr-2 h-4 w-4" /> New Plan</Button>}
      />

      {isLoading ? (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">{[1, 2, 3].map((i) => <div key={i} className="h-80 animate-pulse rounded-2xl bg-white/60" />)}</div>
      ) : plans.length === 0 ? (
        <Card className="flex flex-col items-center justify-center rounded-2xl border-dashed border-navy-100 bg-white/60 py-20 text-center shadow-none">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gold-50"><Tags className="h-8 w-8 text-gold-500" /></div>
          <h3 className="mt-5 font-serif text-xl font-bold text-navy-900">No plans yet</h3>
          <p className="mt-2 max-w-sm text-navy-500">Add your first pricing table to present session packages.</p>
          <Button onClick={openNew} className="mt-6 rounded-full bg-gold-500 text-white hover:bg-gold-600"><Plus className="mr-2 h-4 w-4" /> New Plan</Button>
        </Card>
      ) : (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {plans.map((p) => (
            <Card key={p.id} data-testid={`plan-card-${p.id}`} className={`relative flex flex-col rounded-2xl p-6 shadow-ambient transition-all duration-300 hover:-translate-y-1 hover:shadow-ambient-lg ${p.highlighted ? "border-2 border-gold-400 bg-white" : "border-navy-900/5 bg-white"}`}>
              {p.highlighted && <div className="absolute -top-3 left-6 flex items-center gap-1 rounded-full bg-gold-500 px-3 py-1 text-xs font-semibold text-white"><Star className="h-3 w-3" /> Most Popular</div>}
              <h3 className="font-serif text-xl font-bold text-navy-900">{p.name}</h3>
              <div className="mt-2 flex items-baseline gap-1">
                <span className="font-serif text-4xl font-bold text-navy-900">${p.price}</span>
                <span className="text-sm text-navy-400">{p.currency} · {intervals[p.interval]}</span>
              </div>
              <p className="mt-2 text-sm text-navy-500">{p.description}</p>
              <ul className="mt-4 flex-1 space-y-2">
                {(p.features || []).map((f, i) => (
                  <li key={`${f}-${i}`} className="flex items-start gap-2 text-sm text-navy-700"><Check className="mt-0.5 h-4 w-4 shrink-0 text-gold-600" /> {f}</li>
                ))}
              </ul>
              <div className="mt-5 flex gap-2">
                <Button data-testid={`edit-plan-${p.id}`} variant="outline" size="sm" onClick={() => openEdit(p)} className="flex-1 rounded-full border-navy-100"><Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit</Button>
                <Button data-testid={`delete-plan-${p.id}`} variant="outline" size="sm" onClick={() => setToDelete(p)} className="rounded-full border-navy-100 text-destructive hover:bg-destructive/10"><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto bg-white">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-navy-900">{editing ? "Edit Plan" : "New Plan"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Plan name</Label><Input data-testid="plan-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 focus-visible:ring-gold-500" placeholder="Journey Package" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Price</Label><Input data-testid="plan-price-input" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) || 0 })} className="mt-1 focus-visible:ring-gold-500" /></div>
              <div><Label>Currency</Label><Input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className="mt-1 focus-visible:ring-gold-500" /></div>
              <div>
                <Label>Interval</Label>
                <Select value={form.interval} onValueChange={(v) => setForm({ ...form, interval: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{Object.entries(intervals).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 focus-visible:ring-gold-500" rows={2} /></div>
            <div>
              <Label>Features</Label>
              <div className="mt-1 flex gap-2">
                <Input value={featureInput} onChange={(e) => setFeatureInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addFeature(); } }} placeholder="Add a feature and press Enter" className="focus-visible:ring-gold-500" data-testid="plan-feature-input" />
                <Button type="button" onClick={addFeature} variant="outline" className="rounded-lg border-navy-100"><Plus className="h-4 w-4" /></Button>
              </div>
              <div className="mt-2 space-y-1">
                {form.features.map((f, i) => (
                  <div key={`${f}-${i}`} className="flex items-center justify-between rounded-lg bg-ivory px-3 py-1.5 text-sm text-navy-700">
                    <span className="flex items-center gap-2"><Check className="h-3.5 w-3.5 text-gold-600" /> {f}</span>
                    <button onClick={() => removeFeature(i)} className="text-navy-300 hover:text-destructive"><X className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Button label</Label><Input value={form.cta_label} onChange={(e) => setForm({ ...form, cta_label: e.target.value })} className="mt-1 focus-visible:ring-gold-500" /></div>
              <div><Label>Button link (GHL)</Label><Input value={form.cta_link} onChange={(e) => setForm({ ...form, cta_link: e.target.value })} className="mt-1 focus-visible:ring-gold-500" placeholder="https://..." /></div>
            </div>
            <div className="flex items-center gap-3 rounded-lg bg-ivory px-4 py-2.5">
              <Switch checked={form.highlighted} onCheckedChange={(v) => setForm({ ...form, highlighted: v })} data-testid="plan-highlight-switch" />
              <span className="text-sm text-navy-700">Highlight as "Most Popular"</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
            <Button data-testid="save-plan-button" disabled={!form.name || save.isPending} onClick={() => save.mutate(form)} className="rounded-full bg-gold-500 text-white hover:bg-gold-600">{save.isPending ? "Saving..." : "Save Plan"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader><AlertDialogTitle>Delete plan?</AlertDialogTitle><AlertDialogDescription>"{toDelete?.name}" will be permanently removed.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel><AlertDialogAction data-testid="confirm-delete-plan" onClick={() => del.mutate(toDelete.id)} className="rounded-full bg-destructive hover:bg-destructive/90">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
